import { Box, Text } from "@chakra-ui/react";
import { ReactElement } from "react";
import ErrorPage from "components/Shared/ErrorPage";
import PageLoadingSpinner from "components/Shared/PageLoadingSpinner";
import { useAuth } from "utils/auth";
import { AppMode, useAppMode } from "utils/appMode";
import useAuthToken from "utils/useAuthToken";
import HomeButton from "components/Shared/HomeButton";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";
import { useCurrentUserBrokers } from "utils/currentUserHooks";

type Props = {
  children: ReactElement;
};

export default function PageAccessGate({ children }: Props) {
  const appMode = useAppMode();
  const { isAuthenticated } = useAuth();
  const token = useAuthToken();
  const { brokers, isLoading: isLoadingBrokers } = useCurrentUserBrokers();
  const { isStaff, isSuperuser } = token || {};

  if (!isAuthenticated && typeof window !== "undefined") return children;

  const accessChecks = [
    {
      isMatch: appMode === AppMode.BROKER,
      isLoading: isLoadingBrokers || typeof window === "undefined",
      isAuthorized: brokers?.length > 0 || !!isSuperuser,
    },
    {
      isMatch: appMode === AppMode.ADMIN,
      isLoading: false,
      isAuthorized: isStaff !== false,
    },
  ];

  const activeCheck = accessChecks.find((check) => check.isMatch);

  if (!activeCheck) return children;

  if (activeCheck.isLoading) {
    return <PageLoadingSpinner height={CONTENT_FULL_HEIGHT} />;
  }

  if (activeCheck.isAuthorized) return children;

  return (
    <ErrorPage heading="Access Denied">
      <Text fontSize="xl" fontWeight="700" mt={10}>
        You are attempting to access a page that requires different privileges
        than it appears you have.
      </Text>

      <Box mt={10}>
        <HomeButton />
      </Box>
    </ErrorPage>
  );
}
